import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Modal,
  Row,
  Spinner,
  InputGroup,
} from "react-bootstrap";
import TableContainer from "Common/TableContainer";
import Flatpickr from "react-flatpickr";
import { Link, useNavigate } from "react-router-dom";
import {
  deleteInvoice,
  getAllInvoices,
  updateInvoice,
  generateInvoicePDF,
  sendInvoiceEmail,
} from "api/apis";
import { toast } from "react-toastify";
import moment from "moment";

interface InvoiceListTableProps {
  onDataLoaded?: (data: any[]) => void;
}

interface ProductItem {
  productSnapshot?: {
    name: string;
    description: string;
  };
  productName?: string;
  productDetails?: string;
  karat?: string;
  weight?: number;
  purchasePrice?: number;
  quantity: number;
  totalPrice?: number;
  total?: number;
  variantSku?: string;
  attributes?: Array<{ label: string; value: string }>;
  [key: string]: any;
}

interface PriceBreakdownItem {
  label: string;
  value: number;
  type: string;
  show?: boolean;
  isCharge?: boolean;
  isDiscount?: boolean;
  valueType?: string;
  [key: string]: any;
}

interface EditFormData {
  // Invoice Meta
  invoiceNumber: string;
  date: string;
  dueDate: string;
  paymentStatus: "Unpaid" | "Paid" | "Refund" | "Pending" | "Cancelled";
  paymentMethod: string;
  paymentDetails: string;
  currency: string;
  orderNotes: string;

  // Company Info
  companyName: string;
  companyAddress: string;
  companyPostalCode: string;
  registrationNumber: string;
  companyEmail: string;
  companyWebsite: string;
  companyPhone: string;
  gstin: string;

  // Customer Info
  firstName: string;
  lastName: string;
  clientName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zipCode: string;
  country: string;

  // Addresses
  billingAddress: any;
  shippingAddress: any;

  // Products
  products: ProductItem[];

  // Price Breakdown
  priceBreakdown: PriceBreakdownItem[];

  // Pricing Summary
  subtotal: number;
  totalTax: number;
  discount: number;
  total: number;
  grandTotal?: number;

  // Other
  clientEmail: string;
  clientAddress: string;
}

const InvoiceListTable: React.FC<InvoiceListTableProps> = ({
  onDataLoaded,
}) => {
  const navigate = useNavigate();
  const [modal_AddUserModals, setmodal_AddUserModals] =
    useState<boolean>(false);
  const [isMultiDeleteButton, setIsMultiDeleteButton] =
    useState<boolean>(false);
  const [invoiceData, setInvoiceData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [dateRange, setDateRange] = useState<any>(null);
  const [selectedInvoices, setSelectedInvoices] = useState<string[]>([]);

  // State for modals
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showSendModal, setShowSendModal] = useState<boolean>(false);

  // State for operations
  const [currentInvoice, setCurrentInvoice] = useState<any>(null);
  const [operationLoading, setOperationLoading] = useState<boolean>(false);
  const [emailData, setEmailData] = useState({
    to: "",
    subject: "",
    message: "",
  });

  // Initial edit form data
  const initialEditFormData: EditFormData = {
    // Invoice Meta
    invoiceNumber: "",
    date: "",
    dueDate: "",
    paymentStatus: "Unpaid",
    paymentMethod: "Cash",
    paymentDetails: "",
    currency: "₹",
    orderNotes:
      "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged.",

    // Company Info
    companyName: "Anjal Gold",
    companyAddress:
      "Off No - 13/A, 4th Floor, 97/101, Near Mumbadevi Chambers, Shaikh Memon Street, Zaveri Bazar-Kalbadevi-400002",
    companyPostalCode: "400002",
    registrationNumber: "",
    companyEmail: "info@anjalgold.com",
    companyWebsite: "www.anjalgold.com",
    companyPhone: "",
    gstin: "",

    // Customer Info
    firstName: "",
    lastName: "",
    clientName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    zipCode: "",
    country: "India",

    // Addresses
    billingAddress: "",
    shippingAddress: "",

    // Products
    products: [],

    // Price Breakdown
    priceBreakdown: [
      {
        label: "Subtotal (Base Price)",
        value: 0,
        type: "subtotal",
        show: true,
      },
      { label: "Total Tax", value: 0, type: "tax", show: true },
      { label: "Discount", value: 0, type: "discount", show: true },
      { label: "Final Total", value: 0, type: "total", show: true },
    ],

    // Pricing Summary
    subtotal: 0,
    totalTax: 0,
    discount: 0,
    total: 0,
    grandTotal: 0,

    // Other
    clientEmail: "",
    clientAddress: "",
  };

  // Edit form state
  const [editFormData, setEditFormData] =
    useState<EditFormData>(initialEditFormData);

  // Payment status options
  const paymentStatusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Unpaid", label: "Unpaid" },
    { value: "Refund", label: "Refund" },
  ];

  // Payment method options
  const paymentMethodOptions = [
    { value: "Cash", label: "Cash" },
    { value: "Online", label: "Online" },
  ];

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const res = await getAllInvoices();

      const transformedData = res.invoices.map((invoice: any) => {
        // Extract customer name from firstName and lastName
        const customerName =
          invoice.clientName ||
          (invoice.firstName && invoice.lastName
            ? `${invoice.firstName} ${invoice.lastName}`
            : invoice.firstName || "Unknown Customer");

        return {
          id: invoice.invoiceNumber,
          _id: invoice._id,
          customer: customerName,
          email: invoice.email || invoice.clientEmail || "",
          city: invoice.city || "",
          date: invoice.date || "",
          amt: invoice.total || invoice.grandTotal || 0,
          paystatus: invoice.paymentStatus || "Unpaid",
          rawData: invoice,
        };
      });

      setInvoiceData(transformedData);

      if (onDataLoaded) {
        onDataLoaded(transformedData);
      }
    } catch (error) {
      console.error("Error fetching invoices", error);
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    let result = invoiceData;

    if (searchText) {
      const lowerSearch = searchText.toLowerCase();
      result = result.filter(
        (item) =>
          item.id.toLowerCase().includes(lowerSearch) ||
          item.customer.toLowerCase().includes(lowerSearch) ||
          item.email.toLowerCase().includes(lowerSearch) ||
          item.city.toLowerCase().includes(lowerSearch) ||
          item.paystatus.toLowerCase().includes(lowerSearch),
      );
    }

    if (statusFilter && statusFilter !== "all") {
      result = result.filter((item) => item.paystatus === statusFilter);
    }

    if (dateRange && dateRange.length === 2) {
      const startDate = new Date(dateRange[0]).setHours(0, 0, 0, 0);
      const endDate = new Date(dateRange[1]).setHours(23, 59, 59, 999);

      result = result.filter((item) => {
        const itemDate = new Date(item.date).getTime();
        return itemDate >= startDate && itemDate <= endDate;
      });
    }

    return result;
  }, [invoiceData, searchText, statusFilter, dateRange]);

  // Handle checkbox selection
  const handleCheckboxChange = (invoiceId: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedInvoices((prev) => [...prev, invoiceId]);
    } else {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId));
    }
  };

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkAll") as HTMLInputElement;
    const ele = document.querySelectorAll(
      ".invoiceCheckBox",
    ) as NodeListOf<HTMLInputElement>;

    const allChecked = checkall.checked;
    const selectedIds: string[] = [];

    ele.forEach((ele: HTMLInputElement) => {
      ele.checked = allChecked;
      if (allChecked) {
        selectedIds.push(ele.value);
      }
    });

    setSelectedInvoices(allChecked ? selectedIds : []);
    setIsMultiDeleteButton(allChecked);
  }, []);

  const checkedbox = () => {
    const ele = document.querySelectorAll(".invoiceCheckBox:checked");
    setIsMultiDeleteButton(ele.length > 0);

    const selectedIds: string[] = [];
    ele.forEach((ele: any) => {
      selectedIds.push(ele.value);
    });

    setSelectedInvoices(selectedIds);
  };

  // Handle Edit - FIXED VERSION
  // Handle Edit - FIXED VERSION
  const handleEditClick = (invoice: any) => {
    setCurrentInvoice(invoice);
    const rawData = invoice.rawData;

    // Parse date strings to proper format for input fields
    const parseDateForInput = (dateStr: string) => {
      if (!dateStr) return "";
      try {
        // Handle "17 Jan, 2026" format
        if (dateStr.includes(",")) {
          const date = moment(dateStr, "DD MMM, YYYY").format("YYYY-MM-DD");
          return date;
        }
        // Handle ISO format
        const date = moment(dateStr).format("YYYY-MM-DD");
        return date;
      } catch (error) {
        console.error("Error parsing date:", dateStr, error);
        return "";
      }
    };

    // Transform products to a consistent format
    const transformProducts = (products: any[]) => {
      if (!products || !Array.isArray(products)) return [];

      return products.map((product: any) => {
        // Extract attributes for karat and weight
        let karat = "";
        let weight = 0;

        if (product.attributes && Array.isArray(product.attributes)) {
          product.attributes.forEach((attr: any) => {
            if (attr.label === "Karat") karat = attr.value;
            if (attr.label === "Weight" && attr.value) {
              // Extract numeric value from "10.88" or "10.88g"
              const weightStr = attr.value.toString();
              const weightMatch = weightStr.match(/[\d.]+/);
              weight = weightMatch ? parseFloat(weightMatch[0]) : 0;
            }
          });
        }

        return {
          productName:
            product.productSnapshot?.name || product.productName || "Product",
          productDetails:
            product.productSnapshot?.description ||
            product.productDetails ||
            "",
          karat: karat,
          weight: product.weight || weight,
          purchasePrice:
            product.purchasePrice || product.totalPrice || product.total || 0,
          quantity: product.quantity || 1,
          totalPrice:
            product.total ||
            product.totalPrice ||
            (product.purchasePrice || 0) * (product.quantity || 1),
          variantSku: product.variantSku || "",
          attributes: product.attributes || [],
          productSnapshot: product.productSnapshot,
        };
      });
    };

    // Transform price breakdown
    const transformPriceBreakdown = (breakdown: any[]) => {
      if (!breakdown || !Array.isArray(breakdown)) {
        return [
          {
            label: "Subtotal (Base Price)",
            value: rawData.subtotal || 0,
            type: "subtotal",
            show: true,
          },
          {
            label: "Total Tax",
            value: rawData.totalTax || 0,
            type: "tax",
            show: true,
          },
          {
            label: "Discount",
            value: rawData.discount || 0,
            type: "discount",
            show: true,
          },
          {
            label: "Final Total",
            value: rawData.total || rawData.grandTotal || 0,
            type: "total",
            show: true,
          },
        ];
      }

      return breakdown.map((item: any) => ({
        label: item.label || "",
        value: item.value || 0,
        type: item.type || "other",
        show: item.show !== false,
        isCharge: item.isCharge || false,
        isDiscount: item.isDiscount || item.type === "discount",
        valueType: item.valueType,
      }));
    };

    const products = transformProducts(rawData.products || []);
    const priceBreakdown = transformPriceBreakdown(rawData.priceBreakdown);

    // Calculate totals from price breakdown - FIXED CALCULATION
    let subtotalFromBreakdown = 0;
    let totalCharges = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    priceBreakdown.forEach((item) => {
      if (item.type === "subtotal") {
        subtotalFromBreakdown = item.value || 0;
      } else if (
        item.type === "charge" &&
        "isCharge" in item &&
        item.isCharge
      ) {
        totalCharges += item.value || 0;
      } else if (item.type === "tax") {
        totalTax += item.value || 0;
      } else if (item.type === "discount") {
        totalDiscount += item.value || 0;
      }
    });

    // If no subtotal in breakdown, calculate from products
    if (subtotalFromBreakdown === 0) {
      subtotalFromBreakdown = products.reduce(
        (sum, product) => sum + (product.totalPrice || 0),
        0,
      );
    }

    // Calculate Grand Total (before discount): subtotal + charges + tax
    const grandTotal = subtotalFromBreakdown + totalCharges + totalTax;

    // Calculate Final Total (after discount): grandTotal - discount
    const finalTotal = grandTotal - totalDiscount;

    // Ensure there's a Grand Total and Final Total item in price breakdown
    const finalPriceBreakdown = [...priceBreakdown];

    // Check if grandTotal item exists
    const hasGrandTotal = finalPriceBreakdown.some(
      (item) => item.type === "grandTotal",
    );
    if (!hasGrandTotal) {
      finalPriceBreakdown.push({
        label: "Grand Total",
        value: grandTotal,
        type: "grandTotal",
        show: true,
        isCharge: false,
        isDiscount: false,
      });
    } else {
      // Update existing grand total
      finalPriceBreakdown.forEach((item) => {
        if (item.type === "grandTotal") {
          item.value = grandTotal;
        }
      });
    }

    // Check if total item exists
    const hasTotal = finalPriceBreakdown.some((item) => item.type === "total");
    if (!hasTotal) {
      finalPriceBreakdown.push({
        label: "Final Total",
        value: finalTotal,
        type: "total",
        show: true,
        isCharge: false,
        isDiscount: false,
      });
    } else {
      // Update existing total
      finalPriceBreakdown.forEach((item) => {
        if (item.type === "total") {
          item.value = finalTotal;
        }
      });
    }

    // Debug log
    console.log("Edit click - Calculation breakdown:", {
      subtotalFromBreakdown,
      totalCharges,
      totalTax,
      totalDiscount,
      grandTotal,
      finalTotal,
      priceBreakdownItems: priceBreakdown,
      finalPriceBreakdownItems: finalPriceBreakdown,
    });

    const transformedData: EditFormData = {
      // Invoice Meta
      invoiceNumber: rawData.invoiceNumber || "",
      date: parseDateForInput(rawData.date) || "",
      dueDate: parseDateForInput(rawData.dueDate) || "",
      paymentStatus: rawData.paymentStatus || "Unpaid",
      paymentMethod: rawData.paymentMethod || "Cash",
      paymentDetails: rawData.paymentDetails || "",
      currency: rawData.currency || "₹",
      orderNotes: rawData.orderNotes || initialEditFormData.orderNotes,

      // Company Info
      companyName: rawData.companyName || initialEditFormData.companyName,
      companyAddress:
        rawData.companyAddress || initialEditFormData.companyAddress,
      companyPostalCode:
        rawData.companyPostalCode || initialEditFormData.companyPostalCode,
      registrationNumber: rawData.registrationNumber || "",
      companyEmail: rawData.companyEmail || initialEditFormData.companyEmail,
      companyWebsite:
        rawData.companyWebsite || initialEditFormData.companyWebsite,
      companyPhone: rawData.companyPhone || "",
      gstin: rawData.gstin || "",

      // Customer Info
      firstName: rawData.firstName || "",
      lastName: rawData.lastName || "",
      clientName:
        rawData.clientName ||
        `${rawData.firstName || ""} ${rawData.lastName || ""}`.trim(),
      email: rawData.email || rawData.clientEmail || "",
      phone: rawData.phone || "",
      address:
        rawData.address ||
        rawData.billingAddress ||
        rawData.clientAddress ||
        "",
      city: rawData.city || "",
      zipCode: rawData.zipCode || "",
      country: rawData.country || "India",

      // Addresses
      billingAddress:
        rawData.billingAddress ||
        rawData.address ||
        rawData.clientAddress ||
        "",
      shippingAddress:
        rawData.shippingAddress ||
        rawData.address ||
        rawData.clientAddress ||
        "",

      // Products
      products: products,

      // Price Breakdown - using corrected breakdown
      priceBreakdown: finalPriceBreakdown,

      // Pricing Summary - CORRECTED CALCULATION
      subtotal: subtotalFromBreakdown,
      totalTax: totalTax,
      discount: totalDiscount,
      total: finalTotal, // This is finalTotal (after discount), NOT grandTotal
      grandTotal: grandTotal, // This is grandTotal (before discount)

      // Other
      clientEmail: rawData.clientEmail || rawData.email || "",
      clientAddress: rawData.clientAddress || rawData.address || "",
    };

    console.log("Transformed edit data with correct totals:", transformedData);
    console.log(
      "Verification: Grand Total should be",
      grandTotal,
      "and Final Total should be",
      finalTotal,
    );
    setEditFormData(transformedData);
    setShowEditModal(true);
  };

  const handleEditFormChange = (field: string, value: any) => {
    // Handle pricing fields with calculation
    if (field === "subtotal" || field === "totalTax" || field === "discount") {
      setEditFormData((prev) => {
        const subtotal =
          field === "subtotal" ? parseFloat(value) || 0 : prev.subtotal;
        const totalTax =
          field === "totalTax" ? parseFloat(value) || 0 : prev.totalTax;
        const discount =
          field === "discount" ? parseFloat(value) || 0 : prev.discount;

        // Calculate total charges from price breakdown
        const totalCharges = prev.priceBreakdown
          .filter((item) => item.type === "charge" && item.isCharge)
          .reduce((sum, item) => sum + (item.value || 0), 0);

        // Calculate Grand Total (before discount): subtotal + charges + tax
        const grandTotal = subtotal + totalCharges + totalTax;

        // Calculate Final Total (after discount): grandTotal - discount
        const finalTotal = grandTotal - discount;

        // Update price breakdown
        const updatedBreakdown = prev.priceBreakdown.map((item) => {
          if (item.type === "subtotal") {
            return { ...item, value: subtotal };
          } else if (item.type === "tax") {
            return { ...item, value: totalTax };
          } else if (item.type === "discount") {
            return { ...item, value: discount };
          } else if (item.type === "grandTotal") {
            return { ...item, value: grandTotal };
          } else if (item.type === "total") {
            return { ...item, value: finalTotal };
          }
          return item;
        });

        return {
          ...prev,
          [field]: value,
          total: finalTotal,
          grandTotal: grandTotal,
          priceBreakdown: updatedBreakdown,
        };
      });
    } else {
      // For non-pricing fields, just update
      setEditFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  // Handle product changes
  const handleProductChange = (index: number, field: string, value: any) => {
    const updatedProducts = [...editFormData.products];
    const product = updatedProducts[index];

    updatedProducts[index] = {
      ...product,
      [field]: value,
    };

    // Recalculate product total if price or quantity changes
    if (field === "quantity" || field === "purchasePrice") {
      const purchasePrice =
        field === "purchasePrice"
          ? parseFloat(value) || 0
          : product.purchasePrice || 0;
      const quantity =
        field === "quantity" ? parseInt(value) || 1 : product.quantity || 1;
      const totalPrice = purchasePrice * quantity;

      updatedProducts[index] = {
        ...updatedProducts[index],
        totalPrice: totalPrice,
      };
    }

    setEditFormData((prev) => ({
      ...prev,
      products: updatedProducts,
    }));

    // Recalculate subtotal and total
    const subtotal = updatedProducts.reduce(
      (sum, product) => sum + (product.totalPrice || 0),
      0,
    );

    setEditFormData((prev) => {
      // Calculate total charges from price breakdown
      const totalCharges = prev.priceBreakdown
        .filter((item) => item.type === "charge" && item.isCharge)
        .reduce((sum, item) => sum + (item.value || 0), 0);

      // Calculate Grand Total (before discount): subtotal + charges + tax
      const grandTotal = subtotal + totalCharges + prev.totalTax;

      // Calculate Final Total (after discount): grandTotal - prev.discount
      const finalTotal = grandTotal - prev.discount;

      // Update price breakdown
      const updatedBreakdown = prev.priceBreakdown.map((item) => {
        if (item.type === "subtotal") {
          return { ...item, value: subtotal };
        } else if (item.type === "grandTotal") {
          return { ...item, value: grandTotal };
        } else if (item.type === "total") {
          return { ...item, value: finalTotal };
        }
        return item;
      });

      return {
        ...prev,
        products: updatedProducts,
        subtotal,
        total: finalTotal,
        grandTotal: grandTotal,
        priceBreakdown: updatedBreakdown,
      };
    });
  };

  // Add new product
  const handleAddProduct = () => {
    const newProduct: ProductItem = {
      productName: "New Product",
      productDetails: "",
      karat: "22K",
      weight: 0,
      purchasePrice: 0,
      quantity: 1,
      totalPrice: 0,
      variantSku: "",
    };

    setEditFormData((prev) => ({
      ...prev,
      products: [...prev.products, newProduct],
    }));
  };

  const handleRemoveProduct = (index: number) => {
    const updatedProducts = [...editFormData.products];
    updatedProducts.splice(index, 1);

    // Recalculate totals
    const subtotal = updatedProducts.reduce(
      (sum, product) => sum + (product.totalPrice || 0),
      0,
    );

    // Calculate total charges from price breakdown
    const totalCharges = editFormData.priceBreakdown
      .filter((item) => item.type === "charge" && item.isCharge)
      .reduce((sum, item) => sum + (item.value || 0), 0);

    // Calculate Grand Total (before discount): subtotal + charges + tax
    const grandTotal = subtotal + totalCharges + editFormData.totalTax;

    // Calculate Final Total (after discount): grandTotal - editFormData.discount
    const finalTotal = grandTotal - editFormData.discount;

    const updatedBreakdown = editFormData.priceBreakdown.map((item) => {
      if (item.type === "subtotal") {
        return { ...item, value: subtotal };
      } else if (item.type === "grandTotal") {
        return { ...item, value: grandTotal };
      } else if (item.type === "total") {
        return { ...item, value: finalTotal };
      }
      return item;
    });

    setEditFormData((prev) => ({
      ...prev,
      products: updatedProducts,
      subtotal,
      total: finalTotal,
      grandTotal: grandTotal,
      priceBreakdown: updatedBreakdown,
    }));
  };

  // Handle price breakdown changes
  const handlePriceBreakdownChange = (
    index: number,
    field: string,
    value: any,
  ) => {
    const updatedBreakdown = [...editFormData.priceBreakdown];
    updatedBreakdown[index] = {
      ...updatedBreakdown[index],
      [field]: field === "value" ? parseFloat(value) || 0 : value,
    };

    // Recalculate totals
    let subtotal = 0;
    let totalCharges = 0;
    let totalTax = 0;
    let totalDiscount = 0;

    updatedBreakdown.forEach((item) => {
      if (item.type === "subtotal") {
        subtotal = item.value || 0;
      } else if (item.type === "charge" && item.isCharge) {
        totalCharges += item.value || 0;
      } else if (item.type === "tax") {
        totalTax += item.value || 0;
      } else if (item.type === "discount") {
        totalDiscount += item.value || 0;
      }
    });

    const grandTotal = subtotal + totalCharges + totalTax;
    const finalTotal = grandTotal - totalDiscount;

    // Update total/grand total items in breakdown
    const finalBreakdown = updatedBreakdown.map((item) => {
      if (item.type === "grandTotal") {
        return { ...item, value: grandTotal };
      } else if (item.type === "total") {
        return { ...item, value: finalTotal };
      }
      return item;
    });

    setEditFormData((prev) => ({
      ...prev,
      priceBreakdown: finalBreakdown,
      subtotal,
      totalTax,
      discount: totalDiscount,
      total: finalTotal,
      grandTotal: grandTotal,
    }));
  };

  // Add new price breakdown item
  const handleAddPriceBreakdown = () => {
    const newItem: PriceBreakdownItem = {
      label: "New Item",
      value: 0,
      type: "other",
      show: true,
    };

    setEditFormData((prev) => ({
      ...prev,
      priceBreakdown: [...prev.priceBreakdown, newItem],
    }));
  };

  // Remove price breakdown item
// Remove price breakdown item - FIXED VERSION
const handleRemovePriceBreakdown = (index: number) => {
  const updatedBreakdown = [...editFormData.priceBreakdown];
  const removedItem = updatedBreakdown.splice(index, 1)[0];

  // Recalculate totals based on remaining items
  let subtotal = 0;
  let totalCharges = 0;
  let totalTax = 0;
  let totalDiscount = 0;

  updatedBreakdown.forEach((item) => {
    if (item.type === "subtotal") {
      subtotal = item.value || 0;
    } else if (item.type === "charge" && item.isCharge) {
      totalCharges += item.value || 0;
    } else if (item.type === "tax") {
      totalTax += item.value || 0;
    } else if (item.type === "discount") {
      totalDiscount += item.value || 0;
    }
  });

  // If subtotal is 0, calculate from products
  if (subtotal === 0) {
    subtotal = editFormData.products.reduce(
      (sum, product) => sum + (product.totalPrice || 0),
      0,
    );
  }

  // Calculate Grand Total (before discount): subtotal + charges + tax
  const grandTotal = subtotal + totalCharges + totalTax;

  // Calculate Final Total (after discount): grandTotal - discount
  const finalTotal = grandTotal - totalDiscount;

  // Update price breakdown with correct totals
  const finalBreakdown = updatedBreakdown.map((item) => {
    if (item.type === "grandTotal") {
      return { ...item, value: grandTotal };
    } else if (item.type === "total") {
      return { ...item, value: finalTotal };
    }
    return item;
  });

  // Ensure we have all required items in breakdown
  if (!finalBreakdown.some((item) => item.type === "subtotal")) {
    finalBreakdown.unshift({
      label: "Subtotal (Base Price)",
      value: subtotal,
      type: "subtotal",
      show: true,
    });
  }

  if (!finalBreakdown.some((item) => item.type === "grandTotal")) {
    finalBreakdown.push({
      label: "Grand Total",
      value: grandTotal,
      type: "grandTotal",
      show: true,
      isCharge: false,
      isDiscount: false,
    });
  }

  if (!finalBreakdown.some((item) => item.type === "total")) {
    finalBreakdown.push({
      label: "Final Total",
      value: finalTotal,
      type: "total",
      show: true,
      isCharge: false,
      isDiscount: false,
    });
  }

  setEditFormData((prev) => ({
    ...prev,
    priceBreakdown: finalBreakdown,
    subtotal,
    totalTax,
    discount: totalDiscount,
    total: finalTotal,
    grandTotal: grandTotal,
  }));
};

  const handleEditSave = async () => {
    if (!currentInvoice) return;

    try {
      setOperationLoading(true);

      // Recalculate subtotal from products
      const subtotal = editFormData.products.reduce(
        (sum, product) => sum + (product.totalPrice || 0),
        0,
      );

      // Calculate charges and taxes from price breakdown
      let totalCharges = 0;
      let totalTax = 0;
      let totalDiscount = 0;

      editFormData.priceBreakdown.forEach((item) => {
        if (item.type === "charge" && item.isCharge) {
          totalCharges += item.value || 0;
        } else if (item.type === "tax") {
          totalTax += item.value || 0;
        } else if (item.type === "discount") {
          totalDiscount += item.value || 0;
        }
      });

      // Calculate Grand Total (before discount): subtotal + charges + tax
      const grandTotal = subtotal + totalCharges + totalTax;

      // Calculate Final Total (after discount): grandTotal - discount
      const finalTotal = grandTotal - totalDiscount;

      console.log("Calculation breakdown:", {
        subtotal,
        totalCharges,
        totalTax,
        totalDiscount,
        grandTotal,
        finalTotal,
      });

      // Update price breakdown with correct totals
      const updatedBreakdown = editFormData.priceBreakdown.map((item) => {
        if (item.type === "subtotal") {
          return { ...item, value: subtotal };
        } else if (item.type === "grandTotal") {
          return { ...item, value: grandTotal };
        } else if (item.type === "total") {
          return { ...item, value: finalTotal };
        }
        return item;
      });

      // Prepare update data matching the schema
      const updateData = {
        // Invoice Meta
        invoiceNumber: editFormData.invoiceNumber,
        date: moment(editFormData.date).format("D MMM, YYYY"),
        dueDate: editFormData.dueDate
          ? moment(editFormData.dueDate).format("D MMM, YYYY")
          : "",
        paymentStatus: editFormData.paymentStatus,
        paymentMethod: editFormData.paymentMethod,
        paymentDetails: editFormData.paymentDetails,
        currency: editFormData.currency,
        orderNotes: editFormData.orderNotes,

        // Company Info
        companyName: editFormData.companyName,
        companyAddress: editFormData.companyAddress,
        companyPostalCode: editFormData.companyPostalCode,
        registrationNumber: editFormData.registrationNumber,
        companyEmail: editFormData.companyEmail,
        companyWebsite: editFormData.companyWebsite,
        companyPhone: editFormData.companyPhone,
        gstin: editFormData.gstin,

        // Customer Info
        firstName: editFormData.firstName,
        lastName: editFormData.lastName,
        clientName: editFormData.clientName,
        email: editFormData.email,
        phone: editFormData.phone,
        address: editFormData.address,
        city: editFormData.city,
        zipCode: editFormData.zipCode,
        country: editFormData.country,

        // Addresses
        billingAddress: editFormData.billingAddress,
        shippingAddress: editFormData.shippingAddress,

        // Products - transform back to original format
        products: editFormData.products.map((product) => ({
          productSnapshot: {
            name: product.productName,
            description: product.productDetails || "",
          },
          variantSku: product.variantSku || `SKU-${Date.now()}`,
          attributes: [
            ...(product.karat
              ? [{ label: "Karat", value: product.karat }]
              : []),
            ...(product.weight
              ? [{ label: "Weight", value: `${product.weight}` }]
              : []),
          ],
          weight: product.weight || 0,
          purchasePrice: product.purchasePrice || 0,
          quantity: product.quantity || 1,
          total: product.totalPrice || 0,
        })),

        // Price Breakdown - ensure proper order: Subtotal > Charges > Tax > Grand Total > Discount > Final Total
        priceBreakdown: (() => {
          // Separate items by type for proper ordering
          const subtotalItems = updatedBreakdown.filter(
            (item) => item.type === "subtotal",
          );
          const chargeItems = updatedBreakdown.filter(
            (item) => item.type === "charge",
          );
          const taxItems = updatedBreakdown.filter(
            (item) => item.type === "tax",
          );
          const grandTotalItems = updatedBreakdown.filter(
            (item) => item.type === "grandTotal",
          );
          const discountItems = updatedBreakdown.filter(
            (item) => item.type === "discount",
          );
          const totalItems = updatedBreakdown.filter(
            (item) => item.type === "total",
          );
          const otherItems = updatedBreakdown.filter(
            (item) =>
              ![
                "subtotal",
                "charge",
                "tax",
                "grandTotal",
                "discount",
                "total",
              ].includes(item.type),
          );

          // Return in proper order
          return [
            ...subtotalItems,
            ...chargeItems,
            ...taxItems,
            ...grandTotalItems,
            ...discountItems,
            ...totalItems,
            ...otherItems,
          ];
        })().map((item) => ({
          label: item.label,
          value: item.value,
          type: item.type,
          show: item.show !== false,
          isCharge: item.isCharge || false,
          isDiscount: item.isDiscount || item.type === "discount",
          valueType: item.valueType,
        })),

        // Pricing Summary
        subtotal: subtotal,
        totalTax: totalTax,
        discount: totalDiscount,
        total: finalTotal,
        grandTotal: grandTotal,

        // Other
        clientEmail: editFormData.clientEmail,
        clientAddress: editFormData.clientAddress,

        // Update timestamp
        updatedAt: new Date(),
      };

      console.log("Updated invoice data:", updateData);

      await updateInvoice(currentInvoice._id, updateData);

      toast.success("Invoice updated successfully");
      setShowEditModal(false);
      fetchInvoices();
    } catch (error) {
      console.error("Error updating invoice:", error);
      toast.error("Failed to update invoice");
    } finally {
      setOperationLoading(false);
    }
  };

  // Handle Delete
  const handleDeleteClick = (invoice: any) => {
    setCurrentInvoice(invoice);
    setShowDeleteModal(true);
  };

  const handleMultiDeleteClick = () => {
    if (selectedInvoices.length === 0) {
      toast.warning("Please select invoices to delete");
      return;
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setOperationLoading(true);

      if (currentInvoice) {
        // Single delete
        await deleteInvoice(currentInvoice._id);
        toast.success("Invoice deleted successfully");
      } else if (selectedInvoices.length > 0) {
        // Multiple delete
        const deletePromises = selectedInvoices.map((id) => deleteInvoice(id));
        await Promise.all(deletePromises);
        toast.success(
          `${selectedInvoices.length} invoice(s) deleted successfully`,
        );
        setSelectedInvoices([]);
      }

      setShowDeleteModal(false);
      setCurrentInvoice(null);
      fetchInvoices();
    } catch (error) {
      console.error("Error deleting invoice(s):", error);
      toast.error("Failed to delete invoice(s)");
    } finally {
      setOperationLoading(false);
    }
  };

  // Handle Download
  const handleDownloadClick = async (invoice: any) => {
    try {
      setOperationLoading(true);
      const rawData = invoice.rawData;

      // Prepare invoice data for PDF matching the structure from CreateInvoice
      const invoiceDataForPDF = {
        _id: rawData._id,
        invoiceNumber: rawData.invoiceNumber,
        date: moment(rawData.date).format("D MMM, YYYY"),
        dueDate: rawData.dueDate
          ? moment(rawData.dueDate).format("D MMM, YYYY")
          : "",
        paymentStatus: rawData.paymentStatus,

        // Company info
        companyName: rawData.companyName || "Anjal Gold",
        companyAddress:
          rawData.companyAddress ||
          "Off No - 13/A, 4th Floor, 97/101, Near Mumbadevi Chambers, Shaikh Memon Street, Zaveri Bazar-Kalbadevi-400002",
        companyPostalCode: rawData.companyPostalCode || "400002",
        registrationNumber: rawData.registrationNumber || "",
        companyEmail: rawData.companyEmail || "info@anjalgold.com",
        companyWebsite: rawData.companyWebsite || "www.anjalgold.com",
        companyPhone: rawData.companyPhone || "",

        // Customer info
        firstName: rawData.firstName || "",
        lastName: rawData.lastName || "",
        address: rawData.address || "",
        city: rawData.city || "",
        zipCode: rawData.zipCode || "",
        country: rawData.country || "India",
        phone: rawData.phone || "",
        email: rawData.email || "",

        // Products/Items - using the same structure as CreateInvoice
        products:
          rawData.products?.map((product: any, index: number) => {
            // Extract karat from attributes
            const attributes = product.attributes || [];
            let karat = "";
            let weightFromAttrs = product.weight || 0;

            attributes.forEach((attr: any) => {
              if (attr.label === "Karat" || attr.label === "karat") {
                karat = attr.value;
              }
              if (
                (attr.label === "Weight" || attr.label === "weight") &&
                attr.value
              ) {
                const weightStr = attr.value.toString();
                const weightMatch = weightStr.match(/[\d.]+/);
                weightFromAttrs = weightMatch
                  ? parseFloat(weightMatch[0])
                  : weightFromAttrs;
              }
            });

            return {
              productSnapshot: {
                name:
                  product.productName ||
                  product.productSnapshot?.name ||
                  "Product",
                description:
                  product.productDetails ||
                  product.productSnapshot?.description ||
                  "",
              },
              variantSku: product.variantSku || `SKU-${index + 1}`,
              attributes: [
                ...(karat ? [{ label: "Karat", value: karat }] : []),
                ...(weightFromAttrs > 0
                  ? [{ label: "Weight", value: `${weightFromAttrs}g` }]
                  : []),
                ...(product.karat && !karat
                  ? [{ label: "Karat", value: product.karat }]
                  : []),
                ...(product.weight && weightFromAttrs === 0
                  ? [{ label: "Weight", value: `${product.weight}g` }]
                  : []),
              ],
              weight: weightFromAttrs || product.weight || 0,
              purchasePrice: product.purchasePrice || 0,
              quantity: product.quantity || 1,
              total: product.totalPrice || product.total || 0,
            };
          }) || [],

        // Price breakdown - PROPER ORDERING: Subtotal -> Charges -> Tax -> Grand Total -> Discount -> Final Total
        priceBreakdown: (() => {
          // Get existing breakdown items
          const existingBreakdown = rawData.priceBreakdown || [];

          // Separate items by type
          const subtotalItems = existingBreakdown.filter(
            (item: any) => item.type === "subtotal",
          );
          const chargeItems = existingBreakdown.filter(
            (item: any) => item.type === "charge",
          );
          const taxItems = existingBreakdown.filter(
            (item: any) => item.type === "tax",
          );
          const grandTotalItems = existingBreakdown.filter(
            (item: any) => item.type === "grandTotal",
          );
          const discountItems = existingBreakdown.filter(
            (item: any) => item.type === "discount",
          );
          const totalItems = existingBreakdown.filter(
            (item: any) => item.type === "total",
          );
          const otherItems = existingBreakdown.filter(
            (item: any) =>
              ![
                "subtotal",
                "charge",
                "tax",
                "grandTotal",
                "discount",
                "total",
              ].includes(item.type),
          );

          // If no breakdown exists, create default with proper order
          if (existingBreakdown.length === 0) {
            const subtotal = rawData.subtotal || 0;
            const totalTax = rawData.totalTax || 0;
            const discount = rawData.discount || 0;
            const total = rawData.total || rawData.grandTotal || 0;
            const grandTotal = rawData.grandTotal || subtotal + totalTax;

            return [
              {
                label: "Subtotal (Base Price)",
                value: subtotal,
                type: "subtotal",
                show: true,
                valueType: "fixed",
              },
              {
                label: "Total Tax",
                value: totalTax,
                type: "tax",
                show: true,
                valueType: "fixed",
              },
              {
                label: "Grand Total",
                value: grandTotal,
                type: "grandTotal",
                show: true,
                valueType: "fixed",
              },
              {
                label: "Discount",
                value: discount,
                type: "discount",
                show: true,
                valueType: "fixed",
                isDiscount: true,
              },
              {
                label: "Final Total",
                value: total,
                type: "total",
                show: true,
                valueType: "fixed",
              },
            ];
          }

          // Return in proper order
          return [
            ...(subtotalItems.length > 0
              ? subtotalItems
              : [
                  {
                    label: "Subtotal (Base Price)",
                    value: rawData.subtotal || 0,
                    type: "subtotal",
                    show: true,
                    valueType: "fixed",
                  },
                ]),
            ...chargeItems,
            ...taxItems,
            ...(grandTotalItems.length > 0
              ? grandTotalItems
              : [
                  {
                    label: "Grand Total",
                    value:
                      rawData.grandTotal ||
                      (rawData.subtotal || 0) +
                        chargeItems.reduce(
                          (sum: number, item: any) => sum + (item.value || 0),
                          0,
                        ) +
                        (rawData.totalTax || 0),
                    type: "grandTotal",
                    show: true,
                    valueType: "fixed",
                  },
                ]),
            ...discountItems,
            ...(totalItems.length > 0
              ? totalItems
              : [
                  {
                    label: "Final Total",
                    value: rawData.total || rawData.grandTotal || 0,
                    type: "total",
                    show: true,
                    valueType: "fixed",
                  },
                ]),
            ...otherItems,
          ];
        })().map((item: any) => ({
          label: item.label || "",
          value: item.value || 0,
          type: item.type || "other",
          show: item.show !== false,
          valueType: item.valueType,
          isCharge: item.isCharge || false,
          isDiscount: item.isDiscount || item.type === "discount" || false,
        })),

        // Totals
        subtotal: rawData.subtotal || 0,
        totalTax: rawData.totalTax || 0,
        discount: rawData.discount || 0,
        total: rawData.total || rawData.grandTotal || 0,
        grandTotal:
          rawData.grandTotal ||
          (rawData.subtotal || 0) + (rawData.totalTax || 0),

        // Payment info
        paymentMethod: rawData.paymentMethod || "Cash",
        paymentDetails: rawData.paymentDetails || "",
        currency: rawData.currency || "₹",

        // Notes
        orderNotes:
          rawData.orderNotes ||
          "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged.",

        // Additional fields for email compatibility
        clientName:
          rawData.clientName ||
          `${rawData.firstName || ""} ${rawData.lastName || ""}`.trim(),
        clientAddress: rawData.clientAddress || rawData.address || "",
        clientEmail: rawData.clientEmail || rawData.email || "",
        billingAddress: rawData.billingAddress || rawData.address || "",
        shippingAddress: rawData.shippingAddress || rawData.address || "",
        gstin: rawData.gstin || "",
      };

      console.log("PDF Data with proper breakdown:", {
        priceBreakdown: invoiceDataForPDF.priceBreakdown,
        products: invoiceDataForPDF.products,
        totals: {
          subtotal: invoiceDataForPDF.subtotal,
          totalTax: invoiceDataForPDF.totalTax,
          discount: invoiceDataForPDF.discount,
          total: invoiceDataForPDF.total,
          grandTotal: invoiceDataForPDF.grandTotal,
        },
      });

      await generateInvoicePDF(invoiceDataForPDF);
      toast.success("Invoice downloaded successfully");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice");
    } finally {
      setOperationLoading(false);
    }
  };

  // Handle Send Email
  const handleSendClick = (invoice: any) => {
    setCurrentInvoice(invoice);
    const rawData = invoice.rawData;

    // Prepare invoice data matching the structure from CreateInvoice
    const invoiceDataForEmail = {
      _id: rawData._id,
      invoiceNumber: rawData.invoiceNumber,
      date: moment(rawData.date).format("D MMM, YYYY"),
      dueDate: rawData.dueDate
        ? moment(rawData.dueDate).format("D MMM, YYYY")
        : "",
      paymentStatus: rawData.paymentStatus,

      // Company info
      companyName: rawData.companyName || "Anjal Gold",
      companyAddress:
        rawData.companyAddress ||
        "Off No - 13/A, 4th Floor, 97/101, Near Mumbadevi Chambers, Shaikh Memon Street, Zaveri Bazar-Kalbadevi-400002",
      companyPostalCode: rawData.companyPostalCode || "400002",
      registrationNumber: rawData.registrationNumber || "",
      companyEmail: rawData.companyEmail || "info@anjalgold.com",
      companyWebsite: rawData.companyWebsite || "www.anjalgold.com",
      companyPhone: rawData.companyPhone || "",

      // Customer info
      firstName: rawData.firstName || "",
      lastName: rawData.lastName || "",
      address: rawData.address || "",
      city: rawData.city || "",
      zipCode: rawData.zipCode || "",
      country: rawData.country || "India",
      phone: rawData.phone || "",
      email: rawData.email || "",

      // Products/Items with proper karat extraction
      products:
        rawData.products?.map((product: any, index: number) => {
          // Extract karat from attributes
          const attributes = product.attributes || [];
          let karat = "";
          let weightFromAttrs = product.weight || 0;

          attributes.forEach((attr: any) => {
            if (attr.label === "Karat" || attr.label === "karat") {
              karat = attr.value;
            }
            if (
              (attr.label === "Weight" || attr.label === "weight") &&
              attr.value
            ) {
              const weightStr = attr.value.toString();
              const weightMatch = weightStr.match(/[\d.]+/);
              weightFromAttrs = weightMatch
                ? parseFloat(weightMatch[0])
                : weightFromAttrs;
            }
          });

          return {
            productSnapshot: {
              name:
                product.productName ||
                product.productSnapshot?.name ||
                "Product",
              description:
                product.productDetails ||
                product.productSnapshot?.description ||
                "",
            },
            variantSku: product.variantSku || `SKU-${index + 1}`,
            attributes: [
              ...(karat ? [{ label: "Karat", value: karat }] : []),
              ...(weightFromAttrs > 0
                ? [{ label: "Weight", value: `${weightFromAttrs}g` }]
                : []),
              ...(product.karat && !karat
                ? [{ label: "Karat", value: product.karat }]
                : []),
              ...(product.weight && weightFromAttrs === 0
                ? [{ label: "Weight", value: `${product.weight}g` }]
                : []),
            ],
            weight: weightFromAttrs || product.weight || 0,
            purchasePrice: product.purchasePrice || 0,
            quantity: product.quantity || 1,
            total: product.totalPrice || product.total || 0,
          };
        }) || [],

      // Price breakdown - PROPER ORDERING: Subtotal -> Charges -> Tax -> Grand Total -> Discount -> Final Total
      priceBreakdown: (() => {
        // Get existing breakdown items
        const existingBreakdown = rawData.priceBreakdown || [];

        // Separate items by type
        const subtotalItems = existingBreakdown.filter(
          (item: any) => item.type === "subtotal",
        );
        const chargeItems = existingBreakdown.filter(
          (item: any) => item.type === "charge",
        );
        const taxItems = existingBreakdown.filter(
          (item: any) => item.type === "tax",
        );
        const grandTotalItems = existingBreakdown.filter(
          (item: any) => item.type === "grandTotal",
        );
        const discountItems = existingBreakdown.filter(
          (item: any) => item.type === "discount",
        );
        const totalItems = existingBreakdown.filter(
          (item: any) => item.type === "total",
        );
        const otherItems = existingBreakdown.filter(
          (item: any) =>
            ![
              "subtotal",
              "charge",
              "tax",
              "grandTotal",
              "discount",
              "total",
            ].includes(item.type),
        );

        // If no breakdown exists, create default with proper order
        if (existingBreakdown.length === 0) {
          const subtotal = rawData.subtotal || 0;
          const totalTax = rawData.totalTax || 0;
          const discount = rawData.discount || 0;
          const total = rawData.total || rawData.grandTotal || 0;
          const grandTotal = rawData.grandTotal || subtotal + totalTax;

          return [
            {
              label: "Subtotal (Base Price)",
              value: subtotal,
              type: "subtotal",
              show: true,
              valueType: "fixed",
            },
            {
              label: "Total Tax",
              value: totalTax,
              type: "tax",
              show: true,
              valueType: "fixed",
            },
            {
              label: "Grand Total",
              value: grandTotal,
              type: "grandTotal",
              show: true,
              valueType: "fixed",
            },
            {
              label: "Discount",
              value: discount,
              type: "discount",
              show: true,
              valueType: "fixed",
              isDiscount: true,
            },
            {
              label: "Final Total",
              value: total,
              type: "total",
              show: true,
              valueType: "fixed",
            },
          ];
        }

        // Return in proper order
        return [
          ...(subtotalItems.length > 0
            ? subtotalItems
            : [
                {
                  label: "Subtotal (Base Price)",
                  value: rawData.subtotal || 0,
                  type: "subtotal",
                  show: true,
                  valueType: "fixed",
                },
              ]),
          ...chargeItems,
          ...taxItems,
          ...(grandTotalItems.length > 0
            ? grandTotalItems
            : [
                {
                  label: "Grand Total",
                  value:
                    rawData.grandTotal ||
                    (rawData.subtotal || 0) +
                      chargeItems.reduce(
                        (sum: number, item: any) => sum + (item.value || 0),
                        0,
                      ) +
                      (rawData.totalTax || 0),
                  type: "grandTotal",
                  show: true,
                  valueType: "fixed",
                },
              ]),
          ...discountItems,
          ...(totalItems.length > 0
            ? totalItems
            : [
                {
                  label: "Final Total",
                  value: rawData.total || rawData.grandTotal || 0,
                  type: "total",
                  show: true,
                  valueType: "fixed",
                },
              ]),
          ...otherItems,
        ];
      })().map((item: any) => ({
        label: item.label || "",
        value: item.value || 0,
        type: item.type || "other",
        show: item.show !== false,
        valueType: item.valueType,
        isCharge: item.isCharge || false,
        isDiscount: item.isDiscount || item.type === "discount" || false,
      })),

      // Totals
      subtotal: rawData.subtotal || 0,
      totalTax: rawData.totalTax || 0,
      discount: rawData.discount || 0,
      total: rawData.total || rawData.grandTotal || 0,
      grandTotal:
        rawData.grandTotal || (rawData.subtotal || 0) + (rawData.totalTax || 0),

      // Payment info
      paymentMethod: rawData.paymentMethod || "Cash",
      paymentDetails: rawData.paymentDetails || "",
      currency: rawData.currency || "₹",

      // Notes
      orderNotes:
        rawData.orderNotes ||
        "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged.",

      // Additional fields for email compatibility
      clientName:
        rawData.clientName ||
        `${rawData.firstName || ""} ${rawData.lastName || ""}`.trim(),
      clientAddress: rawData.clientAddress || rawData.address || "",
      clientEmail: rawData.clientEmail || rawData.email || "",
      billingAddress: rawData.billingAddress || rawData.address || "",
      shippingAddress: rawData.shippingAddress || rawData.address || "",
      gstin: rawData.gstin || "",
    };

    // Calculate the actual grand total for the email message
    const actualGrandTotal =
      invoiceDataForEmail.grandTotal ||
      (invoiceDataForEmail.subtotal || 0) +
        invoiceDataForEmail.priceBreakdown
          .filter((item: any) => item.type === "charge")
          .reduce((sum: number, item: any) => sum + (item.value || 0), 0) +
        (invoiceDataForEmail.totalTax || 0);

    setEmailData({
      to: rawData.email || rawData.clientEmail || "",
      subject: `Invoice ${invoice.id} from Anjal Gold`,
      message: `Dear ${invoiceDataForEmail.clientName || "Customer"},

Please find attached your invoice #${invoice.id} dated ${moment(invoice.date).format("D MMM, YYYY")}.

Invoice Details:
- Invoice Number: ${invoice.id}
- Date: ${moment(invoice.date).format("D MMM, YYYY")}
- Subtotal: ₹${(invoiceDataForEmail.subtotal || 0).toFixed(2)}
${invoiceDataForEmail.priceBreakdown
  .filter((item: any) => item.type === "charge" && item.value > 0)
  .map((item: any) => `- ${item.label}: ₹${item.value.toFixed(2)}`)
  .join("\n")}
${invoiceDataForEmail.totalTax > 0 ? `- Total Tax: ₹${invoiceDataForEmail.totalTax.toFixed(2)}` : ""}
- Grand Total: ₹${actualGrandTotal.toFixed(2)}
${invoiceDataForEmail.discount > 0 ? `- Discount: ₹${invoiceDataForEmail.discount.toFixed(2)}` : ""}
- Final Amount: ₹${(invoiceDataForEmail.total || 0).toFixed(2)}
- Payment Status: ${invoice.paystatus}
- Payment Method: ${invoiceDataForEmail.paymentMethod}

You can download the invoice PDF from the attachment.

Thank you for your business!

Best regards,
${invoiceDataForEmail.companyName || "Anjal Gold"}
${invoiceDataForEmail.companyAddress || "Off No - 13/A, 4th Floor, 97/101, Near Mumbadevi Chambers, Shaikh Memon Street, Zaveri Bazar-Kalbadevi-400002"}
Phone: ${invoiceDataForEmail.companyPhone || ""}
Email: ${invoiceDataForEmail.companyEmail || "info@anjalgold.com"}`,
    });

    setShowSendModal(true);
  };

  const handleSendEmail = async () => {
    if (!currentInvoice || !emailData.to) {
      toast.error("Please enter recipient email");
      return;
    }

    try {
      setOperationLoading(true);
      const rawData = currentInvoice.rawData;

      // Prepare invoice data for email matching the CreateInvoice structure
      const invoiceDataForEmail = {
        _id: rawData._id,
        invoiceNumber: rawData.invoiceNumber,
        date: moment(rawData.date).format("D MMM, YYYY"),
        dueDate: rawData.dueDate
          ? moment(rawData.dueDate).format("D MMM, YYYY")
          : "",
        paymentStatus: rawData.paymentStatus,

        // Company info
        companyName: rawData.companyName || "Anjal Gold",
        companyAddress:
          rawData.companyAddress ||
          "Off No - 13/A, 4th Floor, 97/101, Near Mumbadevi Chambers, Shaikh Memon Street, Zaveri Bazar-Kalbadevi-400002",
        companyPostalCode: rawData.companyPostalCode || "400002",
        registrationNumber: rawData.registrationNumber || "",
        companyEmail: rawData.companyEmail || "info@anjalgold.com",
        companyWebsite: rawData.companyWebsite || "www.anjalgold.com",
        companyPhone: rawData.companyPhone || "",

        // Customer info
        firstName: rawData.firstName || "",
        lastName: rawData.lastName || "",
        address: rawData.address || "",
        city: rawData.city || "",
        zipCode: rawData.zipCode || "",
        country: rawData.country || "India",
        phone: rawData.phone || "",
        email: rawData.email || "",

        // Products/Items with proper karat extraction
        products:
          rawData.products?.map((product: any, index: number) => {
            // Extract karat from attributes
            const attributes = product.attributes || [];
            let karat = "";
            let weightFromAttrs = product.weight || 0;

            attributes.forEach((attr: any) => {
              if (attr.label === "Karat" || attr.label === "karat") {
                karat = attr.value;
              }
              if (
                (attr.label === "Weight" || attr.label === "weight") &&
                attr.value
              ) {
                const weightStr = attr.value.toString();
                const weightMatch = weightStr.match(/[\d.]+/);
                weightFromAttrs = weightMatch
                  ? parseFloat(weightMatch[0])
                  : weightFromAttrs;
              }
            });

            return {
              productSnapshot: {
                name:
                  product.productName ||
                  product.productSnapshot?.name ||
                  "Product",
                description:
                  product.productDetails ||
                  product.productSnapshot?.description ||
                  "",
              },
              variantSku: product.variantSku || `SKU-${index + 1}`,
              attributes: [
                ...(karat ? [{ label: "Karat", value: karat }] : []),
                ...(weightFromAttrs > 0
                  ? [{ label: "Weight", value: `${weightFromAttrs}g` }]
                  : []),
                ...(product.karat && !karat
                  ? [{ label: "Karat", value: product.karat }]
                  : []),
                ...(product.weight && weightFromAttrs === 0
                  ? [{ label: "Weight", value: `${product.weight}g` }]
                  : []),
              ],
              weight: weightFromAttrs || product.weight || 0,
              purchasePrice: product.purchasePrice || 0,
              quantity: product.quantity || 1,
              total: product.totalPrice || product.total || 0,
            };
          }) || [],

        // Price breakdown - PROPER ORDERING: Subtotal -> Charges -> Tax -> Grand Total -> Discount -> Final Total
        priceBreakdown: (() => {
          // Get existing breakdown items
          const existingBreakdown = rawData.priceBreakdown || [];

          // Separate items by type
          const subtotalItems = existingBreakdown.filter(
            (item: any) => item.type === "subtotal",
          );
          const chargeItems = existingBreakdown.filter(
            (item: any) => item.type === "charge",
          );
          const taxItems = existingBreakdown.filter(
            (item: any) => item.type === "tax",
          );
          const grandTotalItems = existingBreakdown.filter(
            (item: any) => item.type === "grandTotal",
          );
          const discountItems = existingBreakdown.filter(
            (item: any) => item.type === "discount",
          );
          const totalItems = existingBreakdown.filter(
            (item: any) => item.type === "total",
          );
          const otherItems = existingBreakdown.filter(
            (item: any) =>
              ![
                "subtotal",
                "charge",
                "tax",
                "grandTotal",
                "discount",
                "total",
              ].includes(item.type),
          );

          // If no breakdown exists, create default with proper order
          if (existingBreakdown.length === 0) {
            const subtotal = rawData.subtotal || 0;
            const totalTax = rawData.totalTax || 0;
            const discount = rawData.discount || 0;
            const total = rawData.total || rawData.grandTotal || 0;
            const grandTotal = rawData.grandTotal || subtotal + totalTax;

            return [
              {
                label: "Subtotal (Base Price)",
                value: subtotal,
                type: "subtotal",
                show: true,
                valueType: "fixed",
              },
              {
                label: "Total Tax",
                value: totalTax,
                type: "tax",
                show: true,
                valueType: "fixed",
              },
              {
                label: "Grand Total",
                value: grandTotal,
                type: "grandTotal",
                show: true,
                valueType: "fixed",
              },
              {
                label: "Discount",
                value: discount,
                type: "discount",
                show: true,
                valueType: "fixed",
                isDiscount: true,
              },
              {
                label: "Final Total",
                value: total,
                type: "total",
                show: true,
                valueType: "fixed",
              },
            ];
          }

          // Return in proper order
          return [
            ...(subtotalItems.length > 0
              ? subtotalItems
              : [
                  {
                    label: "Subtotal (Base Price)",
                    value: rawData.subtotal || 0,
                    type: "subtotal",
                    show: true,
                    valueType: "fixed",
                  },
                ]),
            ...chargeItems,
            ...taxItems,
            ...(grandTotalItems.length > 0
              ? grandTotalItems
              : [
                  {
                    label: "Grand Total",
                    value:
                      rawData.grandTotal ||
                      (rawData.subtotal || 0) +
                        chargeItems.reduce(
                          (sum: number, item: any) => sum + (item.value || 0),
                          0,
                        ) +
                        (rawData.totalTax || 0),
                    type: "grandTotal",
                    show: true,
                    valueType: "fixed",
                  },
                ]),
            ...discountItems,
            ...(totalItems.length > 0
              ? totalItems
              : [
                  {
                    label: "Final Total",
                    value: rawData.total || rawData.grandTotal || 0,
                    type: "total",
                    show: true,
                    valueType: "fixed",
                  },
                ]),
            ...otherItems,
          ];
        })().map((item: any) => ({
          label: item.label || "",
          value: item.value || 0,
          type: item.type || "other",
          show: item.show !== false,
          valueType: item.valueType,
          isCharge: item.isCharge || false,
          isDiscount: item.isDiscount || item.type === "discount" || false,
        })),

        // Totals
        subtotal: rawData.subtotal || 0,
        totalTax: rawData.totalTax || 0,
        discount: rawData.discount || 0,
        total: rawData.total || rawData.grandTotal || 0,
        grandTotal:
          rawData.grandTotal ||
          (rawData.subtotal || 0) + (rawData.totalTax || 0),

        // Payment info
        paymentMethod: rawData.paymentMethod || "Cash",
        paymentDetails: rawData.paymentDetails || "",
        currency: rawData.currency || "₹",

        // Notes
        orderNotes:
          rawData.orderNotes ||
          "All gold items are hallmarked as per government regulations. Goods once sold will not be taken back or exchanged.",

        // Additional fields for email compatibility
        clientName:
          rawData.clientName ||
          `${rawData.firstName || ""} ${rawData.lastName || ""}`.trim(),
        clientAddress: rawData.clientAddress || rawData.address || "",
        clientEmail: rawData.clientEmail || rawData.email || "",
        billingAddress: rawData.billingAddress || rawData.address || "",
        shippingAddress: rawData.shippingAddress || rawData.address || "",
        gstin: rawData.gstin || "",
      };

      // Log for debugging
      console.log("Sending email with invoice data:", {
        priceBreakdown: invoiceDataForEmail.priceBreakdown,
        totals: {
          subtotal: invoiceDataForEmail.subtotal,
          totalTax: invoiceDataForEmail.totalTax,
          discount: invoiceDataForEmail.discount,
          total: invoiceDataForEmail.total,
          grandTotal: invoiceDataForEmail.grandTotal,
        },
      });

      const result = await sendInvoiceEmail({
        invoiceData: invoiceDataForEmail,
        to: emailData.to,
        subject: emailData.subject,
        message: emailData.message,
      });

      console.log("Email send result:", result);
      toast.success("Invoice sent successfully");
      setShowSendModal(false);
    } catch (error: any) {
      console.error("Error sending invoice:", error);
      toast.error(
        `Failed to send invoice: ${error.message || "Unknown error"}`,
      );
    } finally {
      setOperationLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      // {
      //   Header: (
      //     <div className="form-check">
      //       <input
      //         className="form-check-input"
      //         type="checkbox"
      //         id="checkAll"
      //         onClick={() => checkedAll()}
      //       />
      //     </div>
      //   ),
      //   Cell: (cellProps: any) => {
      //     return (
      //       <div className="form-check">
      //         <input
      //           className="invoiceCheckBox form-check-input"
      //           type="checkbox"
      //           name="chk_child"
      //           value={cellProps.row.original.id}
      //           checked={selectedInvoices.includes(cellProps.row.original.id)}
      //           onChange={(e) => {
      //             handleCheckboxChange(
      //               cellProps.row.original.id,
      //               e.target.checked,
      //             );
      //             checkedbox();
      //           }}
      //         />
      //       </div>
      //     );
      //   },
      //   id: "#",
      // },
      {
        Header: "Invoice Number",
        accessor: "id",
        Cell: (cellProps: any) => {
          return <p>{cellProps.value}</p>;
        },
        filterable: true,
      },
      {
        Header: "Customer Name",
        accessor: "customer",
        filterable: true,
        Cell: (cellProps: any) => {
          return (
            <div className="d-flex align-items-center gap-2">
              <div className="flex-grow-1 ms-2 user_name">
                {cellProps.value}
              </div>
            </div>
          );
        },
      },
      {
        Header: "Email",
        accessor: "email",
        filterable: true,
      },
      {
        Header: "City",
        accessor: "city",
        filterable: true,
      },
      {
        Header: "Date",
        accessor: "date",
        filterable: true,
        Cell: (cellProps: any) => {
          return cellProps.value
            ? new Date(cellProps.value).toLocaleDateString()
            : "N/A";
        },
      },
      {
        Header: "Amount",
        accessor: "amt",
        filterable: true,
        Cell: (cellProps: any) => {
          return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "INR",
          }).format(cellProps.value);
        },
      },
      {
        Header: "Payment Status",
        accessor: "paystatus",
        filterable: true,
        Cell: (cellProps: any) => {
          switch (cellProps.value) {
            case "Paid":
              return (
                <span className="badge bg-success-subtle text-success">
                  {cellProps.value}
                </span>
              );
            case "Unpaid":
              return (
                <span className="badge bg-warning-subtle text-warning">
                  {cellProps.value}
                </span>
              );
            case "Pending":
              return (
                <span className="badge bg-info-subtle text-info">
                  {cellProps.value}
                </span>
              );
            case "Refund":
              return (
                <span className="badge bg-primary-subtle text-primary">
                  {cellProps.value}
                </span>
              );
            case "Cancelled":
              return (
                <span className="badge bg-danger-subtle text-danger">
                  {cellProps.value}
                </span>
              );
            default:
              return (
                <span className="badge bg-secondary-subtle text-secondary">
                  {cellProps.value}
                </span>
              );
          }
        },
      },
      {
        Header: "Action",
        Cell: (cellProps: any) => {
          const invoice = cellProps.row.original;
          return (
            <React.Fragment>
              <Dropdown>
                <Dropdown.Toggle className="btn btn-soft-secondary btn-sm dropdown btn-icon arrow-none">
                  <i className="ri-more-fill align-middle"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu as="ul" className="dropdown-menu-end">
                  <li>
                    <Dropdown.Item onClick={() => handleEditClick(invoice)}>
                      <i className="ri-pencil-fill align-bottom me-2 text-muted" />
                      Edit
                    </Dropdown.Item>
                  </li>
                  <li>
                    <Dropdown.Item onClick={() => handleDownloadClick(invoice)}>
                      <i className="ri-download-fill align-bottom me-2 text-muted" />
                      Download
                    </Dropdown.Item>
                  </li>
                  <li>
                    <Dropdown.Item onClick={() => handleSendClick(invoice)}>
                      <i className="ri-send-plane-fill align-bottom me-2 text-muted" />
                      Send
                    </Dropdown.Item>
                  </li>
                  <Dropdown.Divider />
                  <li>
                    <Dropdown.Item
                      onClick={() => handleDeleteClick(invoice)}
                      className="text-danger"
                    >
                      <i className="ri-delete-bin-fill align-bottom me-2" />
                      Delete
                    </Dropdown.Item>
                  </li>
                </Dropdown.Menu>
              </Dropdown>
            </React.Fragment>
          );
        },
      },
    ],
    [checkedAll, selectedInvoices],
  );

  return (
    <React.Fragment>
      <Row>
        <Col lg={12}>
          <Card id="invoiceList">
            <Card.Header className="border-0">
              <div className="d-flex align-items-center">
                <h5 className="card-title mb-0 flex-grow-1">Invoices</h5>
                <div className="flex-shrink-0">
                  <div className="d-flex gap-2 flex-wrap">
                    {isMultiDeleteButton && (
                      <Button
                        variant="danger"
                        className="btn-icon"
                        onClick={handleMultiDeleteClick}
                        disabled={operationLoading}
                      >
                        {operationLoading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <i className="ri-delete-bin-2-line"></i>
                        )}
                      </Button>
                    )}
                    <Link to="/invoices-create" className="btn btn-primary">
                      <i className="ri-add-line align-bottom me-1"></i> Create
                      Invoice
                    </Link>
                  </div>
                </div>
              </div>
            </Card.Header>
            <Card.Body className="bg-soft-light border border-dashed border-start-0 border-end-0">
              <form>
                <Row className="g-3">
                  <Col xxl={5} sm={12}>
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search bg-light border-light"
                        placeholder="Search for invoice, customer, email, city or status..."
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </Col>

                  <Col xxl={3} sm={4}>
                    <Flatpickr
                      className="form-control bg-light border-light"
                      placeholder="Select Date"
                      options={{
                        mode: "range",
                        dateFormat: "d M, Y",
                      }}
                      value={dateRange}
                      onChange={(date) => setDateRange(date)}
                    />
                  </Col>

                  <Col xxl={3} sm={4}>
                    <div className="input-light">
                      <select
                        className="form-control"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="">All Status</option>
                        {paymentStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Col>

                  <Col xxl={1} sm={4}>
                    <Button
                      variant="info"
                      type="button"
                      className="w-100"
                      onClick={() => {
                        setSearchText("");
                        setStatusFilter("");
                        setDateRange(null);
                        setSelectedInvoices([]);
                      }}
                    >
                      <i className="ri-equalizer-fill me-1 align-bottom"></i>{" "}
                      Clear
                    </Button>
                  </Col>
                </Row>
              </form>
            </Card.Body>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-5">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : (
                <div>
                  <TableContainer
                    columns={columns}
                    data={filteredData}
                    iscustomPageSize={false}
                    isBordered={false}
                    customPageSize={10}
                    className="custom-header-css"
                    tableClass="table-centered align-middle table-nowrap mb-0"
                    theadClass="text-muted"
                    SearchPlaceholder="Search Products..."
                  />

                  {filteredData.length === 0 && (
                    <div className="noresult">
                      <div className="text-center">
                        <h5 className="mt-2">Sorry! No Result Found</h5>
                        <p className="text-muted mb-0">
                          We've searched more than 150+ invoices We did not find
                          any invoices for your search.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Edit Modal - UPDATED FOR PROPER DATA DISPLAY */}
      <Modal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        centered
        size="xl"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit Invoice #{currentInvoice?.id}
            <small className="text-muted ms-2">
              {currentInvoice?.customer}
            </small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentInvoice && (
            <Form>
              {/* Invoice Meta Section */}
              <div className="mb-4">
                <h5 className="mb-3">Invoice Information</h5>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Invoice Number *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.invoiceNumber}
                        onChange={(e) =>
                          handleEditFormChange("invoiceNumber", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Invoice Date *</Form.Label>
                      <Form.Control
                        type="date"
                        value={editFormData.date}
                        onChange={(e) =>
                          handleEditFormChange("date", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Due Date</Form.Label>
                      <Form.Control
                        type="date"
                        value={editFormData.dueDate}
                        onChange={(e) =>
                          handleEditFormChange("dueDate", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Status *</Form.Label>
                      <Form.Select
                        value={editFormData.paymentStatus}
                        onChange={(e) =>
                          handleEditFormChange("paymentStatus", e.target.value)
                        }
                        required
                      >
                        {paymentStatusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Payment Method</Form.Label>
                      <Form.Select
                        value={editFormData.paymentMethod}
                        onChange={(e) =>
                          handleEditFormChange("paymentMethod", e.target.value)
                        }
                      >
                        {paymentMethodOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Currency</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.currency}
                        onChange={(e) =>
                          handleEditFormChange("currency", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Payment Details</Form.Label>
                  <Form.Control
                    type="text"
                    value={editFormData.paymentDetails}
                    onChange={(e) =>
                      handleEditFormChange("paymentDetails", e.target.value)
                    }
                    placeholder="e.g., UPI ID, Cheque No, Transaction ID"
                  />
                </Form.Group>
              </div>

              {/* Company Info Section */}
              <div className="mb-4">
                <h5 className="mb-3">Company Information</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.companyName}
                        onChange={(e) =>
                          handleEditFormChange("companyName", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Company Email</Form.Label>
                      <Form.Control
                        type="email"
                        value={editFormData.companyEmail}
                        onChange={(e) =>
                          handleEditFormChange("companyEmail", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Company Address</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editFormData.companyAddress}
                    onChange={(e) =>
                      handleEditFormChange("companyAddress", e.target.value)
                    }
                  />
                </Form.Group>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Postal Code</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.companyPostalCode}
                        onChange={(e) =>
                          handleEditFormChange(
                            "companyPostalCode",
                            e.target.value,
                          )
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.companyPhone}
                        onChange={(e) =>
                          handleEditFormChange("companyPhone", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>GSTIN</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.gstin}
                        onChange={(e) =>
                          handleEditFormChange("gstin", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Customer Info Section */}
              <div className="mb-4">
                <h5 className="mb-3">Customer Information</h5>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>First Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.firstName}
                        onChange={(e) =>
                          handleEditFormChange("firstName", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Last Name *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.lastName}
                        onChange={(e) =>
                          handleEditFormChange("lastName", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        value={editFormData.email}
                        onChange={(e) =>
                          handleEditFormChange("email", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.phone}
                        onChange={(e) =>
                          handleEditFormChange("phone", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Form.Group className="mb-3">
                  <Form.Label>Address *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={editFormData.address}
                    onChange={(e) =>
                      handleEditFormChange("address", e.target.value)
                    }
                    required
                  />
                </Form.Group>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>City *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.city}
                        onChange={(e) =>
                          handleEditFormChange("city", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Postal Code *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.zipCode}
                        onChange={(e) =>
                          handleEditFormChange("zipCode", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>Country *</Form.Label>
                      <Form.Control
                        type="text"
                        value={editFormData.country}
                        onChange={(e) =>
                          handleEditFormChange("country", e.target.value)
                        }
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </div>

              {/* Products Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Products / Items</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddProduct}
                  >
                    <i className="ri-add-line me-1"></i> Add Product
                  </Button>
                </div>
                {editFormData.products.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No products added</p>
                  </div>
                ) : (
                  editFormData.products.map((product, index) => (
                    <Card key={index} className="mb-3">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            Product #{index + 1} - {product.productName}
                          </h6>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveProduct(index)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </div>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Product Name *</Form.Label>
                              <Form.Control
                                type="text"
                                value={product.productName}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "productName",
                                    e.target.value,
                                  )
                                }
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Karat</Form.Label>
                              <Form.Control
                                type="text"
                                value={product.karat || ""}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "karat",
                                    e.target.value,
                                  )
                                }
                                placeholder="e.g., 22K, 24K"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>Product Details</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            value={product.productDetails || ""}
                            onChange={(e) =>
                              handleProductChange(
                                index,
                                "productDetails",
                                e.target.value,
                              )
                            }
                            placeholder="Product description"
                          />
                        </Form.Group>
                        <Row>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Weight (g)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                value={product.weight || 0}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "weight",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Unit Price (₹)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                value={product.purchasePrice || 0}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "purchasePrice",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Quantity</Form.Label>
                              <Form.Control
                                type="number"
                                value={product.quantity || 1}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "quantity",
                                    parseInt(e.target.value) || 1,
                                  )
                                }
                              />
                            </Form.Group>
                          </Col>
                          <Col md={3}>
                            <Form.Group className="mb-3">
                              <Form.Label>Total Price (₹)</Form.Label>
                              <Form.Control
                                type="number"
                                step="0.01"
                                value={product.totalPrice || 0}
                                onChange={(e) =>
                                  handleProductChange(
                                    index,
                                    "totalPrice",
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                                readOnly
                                className="bg-light"
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>
                  ))
                )}
              </div>

              {/* Price Breakdown Section */}
              <div className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Price Breakdown</h5>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleAddPriceBreakdown}
                  >
                    <i className="ri-add-line me-1"></i> Add Item
                  </Button>
                </div>
                {editFormData.priceBreakdown.map((item, index) => (
                  <Row key={index} className="mb-2 align-items-center">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Label</Form.Label>
                        <Form.Control
                          type="text"
                          value={item.label}
                          onChange={(e) =>
                            handlePriceBreakdownChange(
                              index,
                              "label",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Type</Form.Label>
                        <Form.Select
                          value={item.type}
                          onChange={(e) =>
                            handlePriceBreakdownChange(
                              index,
                              "type",
                              e.target.value,
                            )
                          }
                        >
                          <option value="subtotal">Subtotal</option>
                          <option value="tax">Tax</option>
                          <option value="discount">Discount</option>
                          <option value="charge">Charge</option>
                          <option value="grandTotal">Grand Total</option>
                          <option value="total">Total</option>
                          <option value="other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label>Value (₹)</Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          value={item.value}
                          onChange={(e) =>
                            handlePriceBreakdownChange(
                              index,
                              "value",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex align-items-end">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemovePriceBreakdown(index)}
                        disabled={[
                          "subtotal",
                          "tax",
                          "discount",
                          "total",
                          "grandTotal",
                        ].includes(item.type)}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </Col>
                  </Row>
                ))}
              </div>

              {/* Totals Summary - FIXED FOR PROPER CALCULATION */}
              <div className="mb-4">
                <h5 className="mb-3">Totals Summary</h5>
                <Card className="bg-light">
                  <Card.Body>
                    <Row className="mb-2">
                      <Col md={6} className="text-end">
                        <strong>Subtotal:</strong>
                      </Col>
                      <Col md={6} className="text-end">
                        <strong>₹{editFormData.subtotal.toFixed(2)}</strong>
                      </Col>
                    </Row>

                    {/* Show additional charges if any */}
                    {editFormData.priceBreakdown
                      .filter(
                        (item) => item.type === "charge" && item.value > 0,
                      )
                      .map((charge, index) => (
                        <Row key={index} className="mb-2">
                          <Col md={6} className="text-end">
                            <span>{charge.label}:</span>
                          </Col>
                          <Col md={6} className="text-end">
                            <span>+ ₹{charge.value.toFixed(2)}</span>
                          </Col>
                        </Row>
                      ))}

                    <Row className="mb-2">
                      <Col md={6} className="text-end">
                        <strong>Total Tax:</strong>
                      </Col>
                      <Col md={6} className="text-end">
                        <strong>+ ₹{editFormData.totalTax.toFixed(2)}</strong>
                      </Col>
                    </Row>
                    <Row className="mb-2">
                      <Col md={6} className="text-end">
                        <strong>Grand Total:</strong>
                      </Col>
                      <Col md={6} className="text-end">
                        <strong>₹{editFormData?.grandTotal?.toFixed(2)}</strong>
                      </Col>
                    </Row>
                    {editFormData.discount > 0 && (
                      <Row className="mb-2">
                        <Col md={6} className="text-end">
                          <strong>Discount:</strong>
                        </Col>
                        <Col md={6} className="text-end">
                          <strong className="text-success">
                            - ₹{editFormData.discount.toFixed(2)}
                          </strong>
                        </Col>
                      </Row>
                    )}

                    <hr />
                    <Row>
                      <Col md={6} className="text-end">
                        <h5 className="mb-0">Final Total:</h5>
                      </Col>
                      <Col md={6} className="text-end">
                        <h5 className="mb-0 text-primary">
                          ₹{Number(editFormData.total).toFixed(2)}
                        </h5>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </div>

              {/* Notes Section */}
              <Form.Group className="mb-3">
                <Form.Label>Order Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={editFormData.orderNotes}
                  onChange={(e) =>
                    handleEditFormChange("orderNotes", e.target.value)
                  }
                  placeholder="Enter any additional notes for the invoice..."
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleEditSave}
            disabled={
              operationLoading ||
              !editFormData.invoiceNumber ||
              !editFormData.date ||
              !editFormData.firstName ||
              !editFormData.lastName ||
              !editFormData.email ||
              !editFormData.phone ||
              !editFormData.address ||
              !editFormData.city ||
              !editFormData.zipCode ||
              !editFormData.country ||
              !editFormData.paymentStatus
            }
          >
            {operationLoading ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {currentInvoice
              ? "Delete Invoice"
              : `Delete ${selectedInvoices.length} Invoice(s)`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentInvoice ? (
            <p>
              Are you sure you want to delete invoice{" "}
              <strong>{currentInvoice.id}</strong>? This action cannot be
              undone.
            </p>
          ) : (
            <p>
              Are you sure you want to delete {selectedInvoices.length} selected
              invoice(s)? This action cannot be undone.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeleteModal(false);
              setCurrentInvoice(null);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
            disabled={operationLoading}
          >
            {operationLoading ? "Deleting..." : "Delete"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Send Email Modal */}
      <Modal
        show={showSendModal}
        onHide={() => setShowSendModal(false)}
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Send Invoice #{currentInvoice?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>To Email *</Form.Label>
                  <Form.Control
                    type="email"
                    value={emailData.to}
                    onChange={(e) =>
                      setEmailData({ ...emailData, to: e.target.value })
                    }
                    placeholder="recipient@example.com"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Invoice Status</Form.Label>
                  <Form.Control
                    type="text"
                    value={currentInvoice?.paystatus || ""}
                    readOnly
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Subject *</Form.Label>
              <Form.Control
                type="text"
                value={emailData.subject}
                onChange={(e) =>
                  setEmailData({ ...emailData, subject: e.target.value })
                }
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Message *</Form.Label>
              <Form.Control
                as="textarea"
                rows={6}
                value={emailData.message}
                onChange={(e) =>
                  setEmailData({ ...emailData, message: e.target.value })
                }
                required
              />
              <Form.Text className="text-muted">
                The invoice PDF will be attached automatically.
              </Form.Text>
            </Form.Group>

            {currentInvoice && (
              <div className="alert alert-info">
                <strong>Invoice Details:</strong>
                <br />
                Invoice #: {currentInvoice.id}
                <br />
                Customer: {currentInvoice.customer}
                <br />
                Date: {moment(currentInvoice.date).format("D MMM, YYYY")}
                <br />
                Amount: ₹{currentInvoice.amt}
                <br />
                Status: {currentInvoice.paystatus}
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSendEmail}
            disabled={
              operationLoading ||
              !emailData.to ||
              !emailData.subject ||
              !emailData.message
            }
          >
            {operationLoading ? "Sending..." : "Send Email"}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};

export default InvoiceListTable;
